import marked from 'marked';

import { Cond } from 'Components/FormComponents';

export function objectToArray(obj, keyPropName) {
  if (!obj) {
    return [];
  }

  return Object.entries(obj).map(([key, value]) => ({
    [keyPropName]: key,
    ...value,
  }));
}

export function objectToScalarArray(obj) {
  if (!obj) {
    return [];
  }

  return Object.entries(obj).map(([name, value]) => ({ name, value }));
}

export function formatDefaultValue(value) {
  if (value === null) {
    return '<none>';
  }

  if (value === '') {
    return '<empty string>';
  }

  if (typeof value === 'string') {
    return '"' + value + '"';
  }

  if (typeof value === 'boolean') {
    return String(value);
  }

  return value;
}

export function formatAuthTypes(typesArray) {
  if (!typesArray || typesArray.length === 0) {
    return null;
  }

  return typesArray.map(type => type.charAt(0).toUpperCase() + type.slice(1)).join(', ');
}

function Markdown({ str }) {
  if (!str) {
    return '';
  }

  return (
    <div innerHTML={marked(str)} />
  );
}

function ParametersComponent({ parameters }) {
  return parameters.map((paramData) => {
    const {
      paramName,
      type,
      description,
      minValue,
      maxValue,
      minLength,
      maxLength,
      defaultValue,
      deprecated,
      alias,
      required,
      requiredForAuthTypeString,
      disallowedForAuthTypeString,
      valuesDescriptions,
      values,
      xmlMainElementName,
      xmlValueAttributeName
    } = paramData;

    if (paramData.hideFromDocs) {
      return null;
    }

    return (
      <tr class={`tr-${paramName}`}>
        <td>
          <p>{paramName}</p>

          <table class="table-param-option">
            <tbody>
              <tr>
                <td><strong>Type</strong></td>
                <td>{type}</td>
              </tr>

              <Cond test={!alias}>
                <Cond test={type === 'integer' && minValue !== undefined}>
                  <tr>
                    <td><strong>Min. Value</strong></td>
                    <td>{minValue}</td>
                  </tr>
                </Cond>

                <Cond test={type === 'integer' && maxValue !== undefined}>
                  <tr>
                    <td><strong>Max. Value</strong></td>
                    <td>{maxValue}</td>
                  </tr>
                </Cond>

                <Cond test={type === 'string' && minLength !== undefined}>
                  <tr>
                    <td><strong>Min. Length</strong></td>
                    <td>{minLength}</td>
                  </tr>
                </Cond>

                <Cond test={type === 'string' && maxLength !== undefined}>
                  <tr>
                    <td><strong>Max. Length</strong></td>
                    <td>{maxLength}</td>
                  </tr>
                </Cond>

                <Cond test={defaultValue !== undefined && !(type === 'object' && defaultValue !== null)}>
                  <tr>
                    <td><strong>Default</strong></td>
                    <td>{formatDefaultValue(defaultValue)}</td>
                  </tr>
                </Cond>
              </Cond>
            </tbody>
          </table>
        </td>
        <td>
          <div class="description-column">
            <Cond test={deprecated}>
              <p><strong>** Deprecated **</strong></p>
            </Cond>

            <Cond test={alias}>
              <p>Deprecated alias of <button type="button" class="btn btn-link btn-doc-link" data-alias={alias}>{alias}</button></p>
            </Cond>

            <Cond test={!alias}>
              <Markdown str={description} />

              <Cond test={required}>
                <strong>Required</strong>
                <br />
                <br />
              </Cond>

              <Cond test={requiredForAuthTypeString}>
                <strong>Required For Authentication Type:&nbsp;&nbsp;</strong>{requiredForAuthTypeString}
                <br /><br />
              </Cond>

              <Cond test={disallowedForAuthTypeString}>
                <strong>Forbidden For Authentication Type:&nbsp;&nbsp;</strong>{disallowedForAuthTypeString}
                <br /><br />
              </Cond>

              <Cond test={type === 'enum'}>
                <Cond test={valuesDescriptions && valuesDescriptions.length > 0}>
                  <p><strong>Allowed Values</strong></p>
                  <table class="enum-values-descriptions">
                    <tbody>
                      {valuesDescriptions?.map(vd => (
                        <tr>
                          <td><strong>{vd.name}</strong></td>
                          <td>{vd.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Cond>
                <Cond test={!valuesDescriptions && values && values.length > 0}>
                  <p>
                    <strong>Allowed Values:&nbsp;&nbsp;</strong>({values?.map(val => `"${val}"`).join(', ')})
                  </p>
                </Cond>
              </Cond>

              <Cond test={type === 'array'}>
                <p><strong>XML Example</strong></p>
                <pre>
                  <code>
                    {`<${paramName}><${xmlMainElementName} ${xmlValueAttributeName}=""/></${paramName}>`}
                  </code>
                </pre>
              </Cond>
            </Cond>
          </div>
        </td>
      </tr>
    );
  });
}

function ResultComponent({ resultDefinition }) {
  if (!resultDefinition || resultDefinition.length === 0) {
    return null;
  }

  return (
    <table class="table table-bordered">
      <tbody>
        <tr>
          <th class="col-xs-3">Name</th>
          <th class="col-xs-9">Description</th>
        </tr>
        {resultDefinition.map((result) => {
          const {
            resultFieldName,
            type,
            description,
            alias,
            requiresParameter,
            csvTitle,
            csvHidden,
            maxVersion,
            minVersion,
            authTypeFilterString
          } = result;

          return (
            <tr class={`tr-${resultFieldName}`}>
              <td>
                <p>{resultFieldName}</p>
                <p><strong>Type</strong> {type}</p>
              </td>
              <td>
                <div class="description-column">
                  <Cond test={alias}>
                    <p>Deprecated alias of <button type="button" class="btn btn-link btn-doc-link" data-alias={alias}>{alias}</button></p>
                  </Cond>

                  <Cond test={!alias}>
                    <Markdown str={description} />
                  </Cond>

                  <Cond test={requiresParameter}>
                    <p><strong>{requiresParameter}</strong> parameter must be set to retrieve this field</p>
                  </Cond>

                  <Cond test={csvTitle}>
                    <p><strong>CSV Column Name</strong> {csvTitle}</p>
                  </Cond>

                  <Cond test={csvHidden}>
                    <p><strong>Unavailable for CSV format</strong></p>
                  </Cond>

                  <Cond test={maxVersion}>
                    <p><strong>No longer available in versions newer than {maxVersion}</strong></p>
                  </Cond>

                  <Cond test={minVersion}>
                    <p><strong>Introduced in version {minVersion}</strong></p>
                  </Cond>

                  <Cond test={authTypeFilterString}>
                    <p>
                      <strong>Available for Authentication Type:</strong> {authTypeFilterString}
                    </p>
                  </Cond>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function MethodComponent({ method }) {
  const {
    methodName,
    description,
    requiresOneOf,
    deprecatedBy,
    allowedAuthTypesString,
    parameters,
    subversions,
    resultDefinition,
    resultDefinitionSubversions,
  } = method;

  const deprecatedString = `** This function has been deprecated and will be removed from future versions of the API. Use ${deprecatedBy} instead **`;

  return (
    <div>
      <h3 class={`method-${methodName}`}>{methodName}</h3>
      <Markdown str={description} />

      <Cond test={allowedAuthTypesString}>
        <p>
          <strong>Allowed for Authentication Type:&nbsp;&nbsp;</strong>{allowedAuthTypesString}
        </p>
      </Cond>

      <Cond test={requiresOneOf}>
        {requiresOneOf?.map((requiredParam, idx) => (
          <p>
            <strong>At least one of the following params must be specified:</strong> {requiredParam.join(', ')}
          </p>
        ))}
      </Cond>

      <Cond test={deprecatedBy}>
        <p>
          <strong>
            {deprecatedString}
          </strong>
        </p>
      </Cond>

      <h4>Parameters</h4>
      <table class="table table-bordered">
        <tbody>
          <tr>
            <th class="col-xs-3">Name</th>
            <th class="col-xs-9">Description</th>
          </tr>
          <ParametersComponent parameters={parameters} />
        </tbody>
      </table>

      <Cond test={subversions}>
        <h4>Result - Default</h4>
        <ResultComponent resultDefinition={resultDefinition} />

        {resultDefinitionSubversions?.map(sub => (
          <div>
            <h4>Result - {subversions.names[sub.subversionName] || sub.subversionName}</h4>
            <ResultComponent resultDefinition={sub.resultDefinition} />
          </div>
        ))}
      </Cond>

      <Cond test={!subversions && resultDefinition}>
        <h4>Result</h4>
        <ResultComponent resultDefinition={resultDefinition} />
      </Cond>
    </div>
  );
}

export function RequestGroupComponent({ requestGroup }) {
  const { API_URL, requestGroupName, requestGroupDefinition } = requestGroup;
  const { description, methods } = requestGroupDefinition;

  return (
    <div class="page-container page-doc-viewer-request-group">
      <h1 class={`requestGroup-${requestGroupName}`}>{requestGroupName}</h1>
      <pre><code>https:{API_URL}{requestGroupName}</code></pre>
      <Markdown str={description} />
      {methods.map(method => {
        return (
          <>
            <MethodComponent method={method} />
            <hr />
          </>
        );
      })}
    </div>
  );
}

export function ErrorMessagesComponent({ errorCodes }) {
  return (
    <div class="page-container">
      <h1>API Errors</h1>
      <table class="table table-bordered">
        <tbody>
          <tr>
            <th class="col-xs-6">Name</th>
            <th class="col-xs-6">Description</th>
          </tr>
          {errorCodes.map((error) => (
            <tr>
              <td><strong>{error.name}</strong></td>
              <td>{error.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
